'use client';

import { useMemo, useState } from 'react';
import { PublicLink } from '@/components/PublicLink';
import { Download, Loader2, Maximize2 } from 'lucide-react';

import { AnimatedContainer } from '@/components/ui/animated-container';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PreviewDataTable } from '@/components/preview/PreviewDataTable';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePublicSession } from '@/hooks/use-public-session';
import { DEFAULT_LAZY_ROWS_PAGE_SIZE, useLazyDatasetRows } from '@/hooks/use-lazy-dataset-rows';
import { useToast } from '@/hooks/use-toast';
import { exportDatasetCsv } from '@/services/templates';
import type { DatasetDetail, DatasetRowsPage } from '@/types/templates';
import { formatTableCellValue } from '@/lib/tableValue';

type DatasetDetailClientProps = {
  dataset: DatasetDetail;
  initialPreviewPage: DatasetRowsPage | null;
};

export function DatasetDetailClient({
  dataset,
  initialPreviewPage,
}: DatasetDetailClientProps) {
  const { isAuthenticated } = usePublicSession();
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const exportAvailable = dataset.exportAvailable !== false && dataset.visibility !== 'public_utility';
  const schemaColumns = dataset.schema.map((field) => field.key);
  const schemaColumnTypes = useMemo(
    () =>
      dataset.schema.reduce<Record<string, string>>((types, field) => {
        types[field.key] = field.type;
        return types;
      }, {}),
    [dataset.schema],
  );
  const previewRows = useLazyDatasetRows({
    datasetId: dataset.id,
    enabled: true,
    pageSize: DEFAULT_LAZY_ROWS_PAGE_SIZE,
    initialPage: initialPreviewPage,
  });
  const previewColumns = previewRows.columns.length > 0 ? previewRows.columns : schemaColumns;

  const handleExport = async () => {
    if (!isAuthenticated) {
      toast({
        variant: 'destructive',
        title: 'Требуется вход',
        description: 'Войдите в аккаунт, чтобы экспортировать датасеты.',
      });
      return;
    }
    if (!exportAvailable) {
      toast({
        variant: 'destructive',
        title: 'Экспорт недоступен',
        description: 'Для этого датасета экспорт CSV пока недоступен.',
      });
      return;
    }

    setExporting(true);
    try {
      const { url } = await exportDatasetCsv(dataset.id);
      if (!url) {
        throw new Error('Отсутствует URL для экспорта.');
      }

      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Не удалось выполнить экспорт',
        description: err instanceof Error ? err.message : 'Не удалось экспортировать датасет.',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <AnimatedContainer animation="fadeIn" className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">Превью и схема</h2>
          <p className="mt-2 text-sm leading-6 text-[var(--site-ink-muted)]">
            {dataset.visibility === 'public_utility'
              ? 'Публичный utility dataset доступен постранично прямо в продукте. Экспорт в V1 отключен.'
              : 'Кэшированные примерные строки доступны прямо в продукте, а экспорт CSV остается способом полного доступа.'}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {dataset.visibility !== 'public_utility' ? (
            <Button
              onClick={handleExport}
              disabled={!isAuthenticated || exporting || !exportAvailable}
            >
              {exporting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Экспорт…
                </span>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Экспорт CSV
                </>
              )}
            </Button>
          ) : null}
          {dataset.visibility === 'public_preview' ? (
            <PublicLink href="/contact">
              <Button variant="outline">Связаться с продажами</Button>
            </PublicLink>
          ) : null}
        </div>
      </div>

      <div className="grid min-w-0 gap-6 lg:grid-cols-[2fr_1fr]">
        <Card className="min-w-0">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <CardTitle>Превью</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPreviewDialogOpen(true)}
                disabled={!previewRows.loadedCount && !previewRows.hasMore}
              >
                <Maximize2 className="h-4 w-4" />
                На весь экран
              </Button>
            </div>
            <CardDescription>
              {dataset.visibility === 'public_utility'
                ? 'Опубликованные реальные строки из public utility dataset.'
                : 'Кэшированные примерные строки. Для полного доступа экспортируйте CSV.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {previewRows.isLoading && previewRows.loadedCount === 0 ? (
              <p className="text-sm text-[var(--site-ink-muted)]">Загрузка строк превью…</p>
            ) : previewRows.error && previewRows.loadedCount === 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-rose-600">{previewRows.error}</p>
                <Button type="button" variant="outline" size="sm" onClick={() => void previewRows.retry()}>
                  Повторить
                </Button>
              </div>
            ) : (
              <PreviewDataTable
                tableId={`${dataset.id}-preview`}
                rows={previewRows.rows}
                columns={previewColumns}
                columnTypes={schemaColumnTypes}
                emptyMessage="Строки превью недоступны."
                hasMore={previewRows.hasMore}
                isLoadingMore={previewRows.isLoading}
                onLoadMore={previewRows.loadNext}
                loadedCount={previewRows.loadedCount}
                totalCount={previewRows.totalRows}
                loadMoreError={previewRows.error}
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Схема</CardTitle>
            <CardDescription>Текущие поля датасета.</CardDescription>
          </CardHeader>
          <CardContent>
            {dataset.schema.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Поле</TableHead>
                    <TableHead>Тип</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dataset.schema.map((field) => (
                    <TableRow key={field.key}>
                      <TableCell>{field.key}</TableCell>
                      <TableCell>{formatTableCellValue(field.type)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-[var(--site-ink-muted)]">Схема недоступна.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="flex h-[90vh] max-w-[95vw] flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Превью датасета</DialogTitle>
            <DialogDescription>
              {dataset.visibility === 'public_utility'
                ? 'Опубликованные реальные строки из public utility dataset.'
                : 'Кэшированные примерные строки. Для полного доступа экспортируйте CSV.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto rounded-[1.5rem] border border-[color:var(--site-border-soft)] p-4">
            {previewRows.isLoading && previewRows.loadedCount === 0 ? (
              <p className="text-sm text-[var(--site-ink-muted)]">Загрузка строк превью…</p>
            ) : previewRows.error && previewRows.loadedCount === 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-rose-600">{previewRows.error}</p>
                <Button type="button" variant="outline" size="sm" onClick={() => void previewRows.retry()}>
                  Повторить
                </Button>
              </div>
            ) : (
              <PreviewDataTable
                tableId={`${dataset.id}-preview-dialog`}
                rows={previewRows.rows}
                columns={previewColumns}
                columnTypes={schemaColumnTypes}
                emptyMessage="Строки превью недоступны."
                hasMore={previewRows.hasMore}
                isLoadingMore={previewRows.isLoading}
                onLoadMore={previewRows.loadNext}
                loadedCount={previewRows.loadedCount}
                totalCount={previewRows.totalRows}
                loadMoreError={previewRows.error}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AnimatedContainer>
  );
}
